import { Button, Card, Dialog, DialogPanel, Select, SelectItem, Textarea, TextInput } from "@tremor/react";
import { Layers, Pencil, Plus, Trash2, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import Badge from "../components/Badge.jsx";
import { useServices, useCreateService, useUpdateService, useDeleteService } from "../queries/services.js";
import { useTeams } from "../queries/teams.js";

const EMPTY_FORM = { name: "", description: "", team_id: "" };

function ServiceEditor({ service, teams, onClose, onSaved }) {
  const { t } = useTranslation("services");
  const isEdit = !!service;
  const [form, setForm] = useState(() => ({
    ...EMPTY_FORM,
    ...service,
    team_id: service?.team_id ?? "",
  }));
  const createService = useCreateService();
  const updateService = useUpdateService();
  const saving = createService.isPending || updateService.isPending;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error(t("errors.name_required"));
      return;
    }
    const payload = {
      name: form.name.trim(),
      description: form.description?.trim() || null,
      team_id: form.team_id || null,
    };
    try {
      if (isEdit) {
        await updateService.mutateAsync({ id: service.id, data: payload });
        toast.success(t("success_save"));
      } else {
        await createService.mutateAsync(payload);
        toast.success(t("success_create"));
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.error ?? t("errors.save"));
    }
  }

  return (
    <Dialog open onClose={onClose} static>
      <DialogPanel className="fixed inset-y-0 right-0 m-0 flex h-full w-full max-w-md flex-col rounded-none bg-white p-0 shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-base font-semibold text-gray-900">
            {isEdit ? t("editor.title_edit") : t("editor.title_new")}
          </h2>
          <button type="button" onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">{t("editor.field_name")}</label>
              <TextInput
                required
                value={form.name}
                onValueChange={(v) => setForm((p) => ({ ...p, name: v }))}
                placeholder={t("editor.field_name_placeholder")}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                {t("editor.field_description")} <span className="font-normal text-gray-400">{t("optional")}</span>
              </label>
              <Textarea
                rows={2}
                value={form.description ?? ""}
                onValueChange={(v) => setForm((p) => ({ ...p, description: v }))}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">{t("editor.field_team")}</label>
              <Select value={form.team_id} onValueChange={(v) => setForm((p) => ({ ...p, team_id: v }))}>
                <SelectItem value="">{t("editor.field_team_none")}</SelectItem>
                {teams.map((team) => (
                  <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                ))}
              </Select>
            </div>
          </div>
          <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
            <Button variant="secondary" type="button" onClick={onClose}>{t("cancel")}</Button>
            <Button type="submit" disabled={saving}>
              {saving ? t("saving") : isEdit ? t("editor.save") : t("editor.create")}
            </Button>
          </div>
        </form>
      </DialogPanel>
    </Dialog>
  );
}

export default function Services() {
  const { t } = useTranslation("services");
  const { data, isLoading, isError } = useServices();
  const teamsQuery = useTeams();
  const deleteService = useDeleteService();
  const services = data ?? [];
  const teams = teamsQuery.data ?? [];
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  function openCreate() {
    setEditing(null);
    setEditorOpen(true);
  }
  function openEdit(service) {
    setEditing(service);
    setEditorOpen(true);
  }

  async function handleDelete(service) {
    if (!window.confirm(t("delete_confirm", { name: service.name }))) return;
    try {
      await deleteService.mutateAsync(service.id);
      toast.success(t("success_delete"));
    } catch {
      toast.error(t("errors.delete"));
    }
  }

  return (
    <div>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t("title")}</h1>
          <p className="mt-1 text-sm text-gray-500">{t("subtitle")}</p>
        </div>
        <Button icon={Plus} onClick={openCreate}>{t("new")}</Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-400">{t("loading")}</p>
      ) : isError ? (
        <p className="text-sm text-red-500">{t("errors.load")}</p>
      ) : services.length === 0 ? (
        <Card className="px-5 py-12 text-center">
          <Layers className="mx-auto mb-3 h-8 w-8 text-gray-300" />
          <p className="text-sm font-medium text-gray-700">{t("empty.title")}</p>
          <p className="mt-1 text-sm text-gray-400">{t("empty.description")}</p>
          <Button className="mt-4" icon={Plus} onClick={openCreate}>{t("new")}</Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {services.map((service) => (
            <Card key={service.id} className="p-0">
              <div className="flex items-center gap-4 px-5 py-4">
                <Layers className="h-8 w-8 shrink-0 rounded-lg bg-teal-50 p-1.5 text-teal-600" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold text-gray-900">{service.name}</p>
                    {service.team_name ? (
                      <Badge color="teal">{service.team_name}</Badge>
                    ) : (
                      <Badge color="gray">{t("unowned")}</Badge>
                    )}
                  </div>
                  {service.description && <p className="mt-0.5 truncate text-sm text-gray-500">{service.description}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <Button size="xs" variant="secondary" icon={Pencil} onClick={() => openEdit(service)}>{t("edit")}</Button>
                  <Button size="xs" variant="secondary" color="red" icon={Trash2} onClick={() => handleDelete(service)}>
                    {t("delete")}
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {editorOpen && (
        <ServiceEditor
          service={editing}
          teams={teams}
          onClose={() => setEditorOpen(false)}
          onSaved={() => setEditorOpen(false)}
        />
      )}
    </div>
  );
}
